import { useQuery } from "@tanstack/react-query";
import type { ReportData } from "@school-sales-ai/shared";
import { api } from "@/lib/api";

// Date-window presets for the Reports page. Each maps to an optional `from`
// bound (ISO); `to` is always open-ended (up to now). "all" sends neither.
export type RangeKey = "30d" | "90d" | "year" | "all";

export const RANGE_LABELS: Record<RangeKey, string> = {
  "30d": "30 días",
  "90d": "90 días",
  year: "Este año",
  all: "Todo",
};

// Translates a preset into the `from`/`to` query params. Kept pure so the page
// can key the query on the resolved bounds.
export function resolveRange(key: RangeKey): { from?: string; to?: string } {
  const now = new Date();
  if (key === "all") return {};
  if (key === "year") {
    return { from: new Date(now.getFullYear(), 0, 1).toISOString() };
  }
  const days = key === "30d" ? 30 : 90;
  return { from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString() };
}

/**
 * Consolidated Reports overview (KPIs + product/cycle demand + captured-field
 * distribution + needs-attention), scoped to the selected date window. One query
 * powers the whole page; the range key is part of the cache key so switching
 * windows refetches cleanly.
 */
export function useReportOverview(
  rangeKey: RangeKey,
): ReturnType<typeof useQuery<ReportData>> {
  const range = resolveRange(rangeKey);
  const qs = new URLSearchParams(
    Object.entries(range).filter(([, v]) => v !== undefined) as [string, string][],
  ).toString();

  return useQuery<ReportData>({
    queryKey: ["report-overview", rangeKey],
    queryFn: () => api.get<ReportData>(`/crm/reports/overview${qs ? `?${qs}` : ""}`),
    staleTime: 60_000,
  });
}
